import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

/* --------------------------------------------------
   Global fonts
-------------------------------------------------- */
const GlobalStyles = createGlobalStyle`
  @font-face {
    font-family: 'Anybody';
    src: url('../assets/fonts/Anybody-Regular.ttf') format('truetype');
    font-display: swap;
  }
  @font-face {
    font-family: 'AnybodyMedium';
    src: url('../assets/fonts/Anybody-Medium.ttf') format('truetype');
    font-weight: 500;
    font-display: swap;
  }
  @font-face {
    font-family: 'LexendMega';
    src: url('../assets/fonts/LexendMega-Regular.ttf') format('truetype');
    font-display: swap;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    font-family: 'Anybody', sans-serif;
    background: #fff;
    color: #000;
  }
`;

/* --------------------------------------------------
   Breakpoints (REM‑based for easy scaling)
-------------------------------------------------- */
const breakpoints = {
  mobile: '40rem', // 640px
  tablet: '64rem', // 1024px
};

/* --------------------------------------------------
   Layout containers
-------------------------------------------------- */
const Page = styled.article`
  max-width: 110rem; /* 1440px */
  margin: 0 auto;
  padding: 0 0rem;
  overflow-x: hidden;
  font-family: 'AnybodyMedium', 'Anybody', sans-serif;
`;

const VideoHero = styled.video`
  width: 100%;
  height: auto;
  display: block;
  aspect-ratio: 16 / 9;
`;

const WideImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

/* --------------------------------------------------
   Text styles
-------------------------------------------------- */
const Credits = styled.p`
  text-align: center;
  font-family: 'LexendMega', 'Anybody', sans-serif;
  font-size: clamp(0.75rem, 1vw, 1rem); /* ~12‑16px */
  letter-spacing: 0.05em;
  color: #fff;
  background: #000;
  padding: 0.5rem 1rem;
  width: fit-content;
  margin: 1.5rem auto 0;
`;

const Lead = styled.h2`
  text-align: center;
  color:rgb(196, 55, 0);
  font-weight: 700;
  font-size: clamp(1.125rem, 2.2vw, 1.5625rem); /* 18‑25px */
  line-height: 1.35;
  max-width: 70ch;
  margin: 2rem auto;
  margin-top: 0;
`;

const Para = styled.p`
  font-size: clamp(1rem, 1.6vw, 1.5625rem); /* 16‑25px */
  line-height: 1.35;
  max-width: 80ch;
  margin: 1.25rem auto;
  text-align: center;
`;

const ItalicPara = styled(Para)`
  font-style: italic;
`;

const StrongPara = styled(Para)`
  font-weight: 700;
`;

/* --------------------------------------------------
   Hamburger icon (3 bars)
-------------------------------------------------- */
const NavIcon = styled.button`
  position: absolute;
  top: 2rem;
  left: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: transparent;
  border: none;
  padding: 0;

  span {
    display: block;
    width: 3rem; /* 48px */
    height: 0.25rem; /* 4px */
    background: #004AAD;
  }
`;

/* --------------------------------------------------
   Spotify iframe
-------------------------------------------------- */
const SpotifyFrame = styled.iframe`
  display: block;
  border-radius: 0.75rem; /* 12px */
  width: 100%;
  height: 35rem; /* 560px */
  max-width: 120ch;
  margin: 3rem auto;
`;

/* --------------------------------------------------
   Footer
-------------------------------------------------- */
const FooterBar = styled.footer`
background: #000;
padding: clamp(3rem, 1vh, 3rem) 0;
text-align: center;

h1 {
  font-family: 'AnybodyBlack', 'Anybody', sans-serif;
  color: #fff;
  font-size: clamp(2.6rem, 8vw, 8rem);
  font-weight: 900;
  margin: 0;
  line-height: 1;
}
`;

/* ==================================================
   Component
================================================== */
export default function Sanchez() {
  return (
    <Page>
      <GlobalStyles />
      {/* Header image added per request */}
      <WideImg src="/assets/sancezheader.png" alt="Frédéric Sanchez header" />
      <Lead>
        SOUND ILLUSTRATOR FRÉDÉRIC SANCHEZ HAS SONICALLY ORCHESTRATED ALMOST EVERY FASHION SHOW YOU CAN THINK OF. GABRIEL MEALOR‑PRITCHARD SITS DOWN WITH THE MULTIHYPHENATE TO DISCUSS HIS LIFE IN MUSIC.
      </Lead>
      {/* Hero video */}
      <VideoHero
        src="/assets/sanchez1.mp4"
        autoPlay
        muted
        loop
        playsInline
/>


<br />

      {/* Article body */}
      <Para>
      When it comes to Frédéric Sanchez, the terms “musician” and “DJ” don't merely begin to do justice to what this creative has achieved in curating sonic landscapes for countless fashion shows and exhibitions since his career began in 1988. Preferring to be referred to as a Sound Illustrator, Sanchez is inspired by experimental French radio hosts like Pierre Henry and Pierre Schaeffer, who pushed the boundaries of on-air noise. "In the 50s and 60s, there were incredible groups of artists and musicians who were in the world of radio, but they were doing experimental things, with tape recorders and microphones," Sanchez tells me. This inspired him to purchase a reel to reel machine in the early stages of his career. Not to mix regularly, but rather cut the tapes "like you do in cinema, and for me, it was this idea that you mix images," he explains, that made him fall in love with the sound of visuals. Images play an integral role in his production process; garnered through extensive research and intimate conversation with the designers, these are the crucial elements that complement the sounds he crafts so well. "That's why I took the name of 'Sound Illustrator', because Sound illustrators were the people in the radio who knew that they were telling stories with sounds. That's where all this comes together," he says.
      </Para>
      <Para>
      For Sanchez, it's about telling a story. It is "very much my thing," he told me as he indulged in how his journey into sound illustration began when Martin Margiela approached him to compose the soundtrack for his debut show in October of '88. "I met Martin Margiela and I mean, that was very, very important," he recalls. "This collaboration that we started together made me think about a lot," he said, prompting him to evaluate where he saw his future heading, the path he would soon take was clearly set out in front of him, at only twenty-two years of age, he designed the sound for Maison Margiela's first ever fashion show.
      </Para>

      <Para>
      Storytelling becomes a theme throughout our conversation. The creative speaks of sound as a narrative thread, what animates fabric, and the space the collection is presented within. "Sound can emphasise, but it can also amplify the wrong thing," he notes, acknowledging the responsibility that comes with shaping a mood. I ask if he implements a narrative arc into his pieces and if he ever gets the opportunity to tell the story of the clothes in his own way. To which he responds, "It's never in my way. It's in the way that I work with the designer. It's the most important thing is for me is that we work together. It's collaborative." "With Miuccia Prada, we have incredible conversations during work. I think now, with Duran Lantink, I have the same sort of conversation and it's working really well with Craig Green too," Sanchez laments, highlighting the importance the illustrator places on working with those with whom he shares mutual interests. "All designers have in common this... the idea of vision," he adds, "It's always nice to work with people who you have the same mindset as, because you collaborate much better with somebody who shares the same vision."
      </Para>
      <br />
      <br />
      {/* Italic pull‑quote */}
      <ItalicPara>"But what makes music, fashion show music?" I ask, intrigued.</ItalicPara>

      <br />
      <br />
      <Para>
      To which the answer is simple, "maybe trying not to make it too fashion," Sanchez chuckles. "No, I mean, any music can work. It depends on the story and what you do. I have no limit," he proclaims. Sanchez is known for his synesthetic approach to music creation and is often depicted as catalysing synesthesia. (For more on this topic, check out our exclusive interview with Richard Butler!) This is due to the compositional take on how he crafts his pieces. As mentioned, the idea of image is so crucial to his process, and to know that the music is not only derived from image but also evokes them is profound. "I come back to the Margiela show, because I put microphones backstage [...] showing what was behind, and I think sound is very much this," he illuminates. To which I reply, "It tells the story of not only what you're seeing, but what has come before it." "Exactly!" He smiles. Sanchez leans forward in his seat. His fascination with what can be inferred through music is evident across his work, he uses his illustrations not just to carry a collection, but to allow it to be memorable, elevating the garments through layered textures and connecting to the designs on a sonic level. "I like to play with a good taste and bad taste," he tells me, "it's what is going to tell the theme."
      </Para>

      <Para>
      "Now, in shows, they're often in incredible places, beautiful venues, but most of the time, the only decoration is the sound," he says frankly, before telling me an anecdote that unfolded between him and Rei Kawakubo of Comme des Garçons. "There was this sort of a problem of language," he says, "So she was really showing the clothes. I had to see the clothes, and actually, it was interesting." It was one of the times that the piece Sanchez had made for the show was based closely on the clothes, as opposed to where the show was being held or through intrinsic research. For the French creative, there is a clear importance between "the clothes and how they react in relation to the space and the architecture," they are set to be presented in.
      </Para>
      <Para>
      In terms of a more innovative method of playing with space, Sanchez describes when he worked with Francesco Risso for Marni in 2019. "I was, you know, designing the space to make the sound. I did that where the sound was turning in the spaces and was creating movement." Partnered with the motion on the catwalk, Sanchez layered punchy synths and breakbeats into the soundscape that whirled around the room, leaving a distinctive mark on the show's rhythm and energy.
      </Para>
      <br />
      {/* ... (Article continues – you can split further paragraphs as needed) ... */}
      <ItalicPara>"I also recently did that for an exhibition for Loewe, in Shanghai and in Tokyo, too. Before, I did the same for Jil Sander, for a huge exhibition in Frankfurt, where the sound was moving within the space. I very much like this idea of layers, and I think there is a lot about that in my work; my work creates layers, layers of emotions," says Sanchez.</ItalicPara>
      {/* Album picks section */}
            {/* Feature image / second video */}
            <VideoHero
        src="/assets/sanchez2.mp4"
        autoPlay
        muted
        loop
        playsInline
/>

<br />
      <Para>
      Sanchez delves into his research process and tells a story of how he was inspired by Nina Simone, a video of her performing he found whilst engrossed in a spiral of YouTube he had fallen into, as he calls it a "good source of information!" Stating, "I was not really much into Nina Simone, [...] but there was a moment where she becomes kind of crazy. She tells very strange stories and she's looking for David Bowie in the audience, but he's not there... really weird things," he says. "It's interesting because during these 15 minutes, she's crazy, but at the end she starts to sing a song, a cover of Stars by Janis Ian, and you understand that she was not, she puts people in the mood," he explains. Simone had captured her audience through stories that evoked the sentiments of her next song. A true performer. Sanchez took this as a source of inspiration and put it towards a project he was working on at the time with Hermès, alongside Nadège Vanhée-Cybulski. The two of them were obsessed with this idea of soul music, jazz and blues, and suddenly it all clicked together; the research had fallen into place. Using what he had found and inputting it into his work to capture the audience and get them just as obsessed as they had both found themselves with the feeling of music. As a sound illustrator, he had formed a relationship with the audience through a soundtrack.
      </Para>
      <Para>
      Then the big questions came: what has been his favourite piece that he has illustrated? "I always say that my favourite ones are the first ones," he begins to explain. "My first show for Margiela, the first show I did for Prada, the first show I did for Jil Sander, all of the first shows," as this is where their stories began. He has been there since their beginnings, watching them grow their brands as he works closely at their side, "There's something quite emotional," says Sanchez. "I have strong, long relationships with these houses, and I'm very happy because now I'm getting this feeling again with Duran, with Jonathan Anderson, and it's nice to be at the start of a story," he says affectionately. With these rising designers of the new age, Sanchez finds himself rediscovering that same energy once found in the winter of 1988, the thrill of co-creating something from the ground up. Furthermore, it affirms his belief in sound as a tool for narrative, as he aids the shaping of a brand's identity from the very first note.
      </Para>
      <Para>
      Frederic Sanchez is one of a kind. In a world where fashion moves so fast and substance is everything, Sanchez holds the power to set the industry to his own tempo. Be it through designing sound as a decorative element for a room, or to capture what lies beneath and translate it into sound, the creative has made his mark over the years as a foundational figure in the music industry. Each of his projects seems less of a commission and more of a conversation that resonates across collections and brand representation, garnering closely-knit relationships and fostering new connections that find their way to him through mutual vision. Vive la musique et vive les histoires!
      </Para>
      <StrongPara>
      FRÉDÉRIC SANCHEZ SELECTS HIS TOP THREE FAVOURITE ALBUMS AND GIVES US THE RUNDOWN ON WHY THEY WERE CHOSEN. LISTEN BELOW:
      </StrongPara>

      <Para>
        <strong>Rock Bottom by Robert Wyatt - </strong>“It's very special. I really fell in love with his voice, it's really beautiful, and I also really like that he is an incredible musician. The way he mixes, the way he creates his songs... It's very charming, poetic, and at the same time, there's a bit of surrealism to it. His voice is incredible. I always come back to Robert Wyatt.”
      </Para>
      <Para>
        <strong>Another Green World by Brian Eno - </strong>“This is also something from my youth. I like the cover of the record, and I like the way he builds the music and the sounds. It has this sort of simplicity, and at the same time, a lot of research. [And he has] a way to use the synthesiser that is very sophisticated in a way that it's naive and at the same time, like a painting.”
      </Para>
      <Para>
        "Anything from <strong>Debussy</strong>," he says to finish.
      </Para>

      {/* Spotify playlist */}
      <SpotifyFrame
        src="https://open.spotify.com/embed/playlist/5sUXJ8rF6X3bSBDZ3pJeSg?utm_source=generator"
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        loading="lazy"
        title="Frédéric Sanchez playlist"
      />



      {/* (You can continue adding the remaining interview paragraphs following the same pattern) */}

      {/* Footer */}
      <FooterBar>
        <h1>RIFF MAGAZINE</h1>
      </FooterBar>
    </Page>
  );
}
